import { Button } from '@/ui/button'
import * as React from 'react'

import { useAuth } from 'react-oidc-context'
import { useNavigate } from 'react-router-dom'
import { saveUserInfo } from 'app/services/local/roleService'
import { RolePermissionModel } from 'app/models/rolePermissionModel'
import { UserInfoModel } from 'app/models/userInfoModel'
import { CompanyDetailsModel } from 'app/models/companyDetailsModel'
import { ROUTES } from 'app/constants/routes'
import { homeRoutesMap } from '@/modules/home/routes'
import { Loader } from '@/ui'
import { NON_RESTORABLE_ROUTES, redirectToLogin } from '@/config'
import { AuthLayout } from '@/app/layouts/AuthLayout'
import { Alert } from '@/Alert'

export type PostLoginDispatchProps = {
  getUserInfo: (userId?: string) => Promise<UserInfoModel>
  getRoles: () => Promise<Array<RolePermissionModel>>
  getCompanies: () => Promise<Array<CompanyDetailsModel>>
  enterCompany: (company: CompanyDetailsModel) => void
}

// Warstwa baseService przy 500/504/timeout obsługuje błąd "wewnętrznie" i NIGDY
// nie rozstrzyga promise (handleServiceError kończy się bez resolve/reject) —
// zwykły catch tego nie złapie. Race z timeoutem to jedyna pewna siatka.
const BOOTSTRAP_TIMEOUT_MS = 20000

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('postLogin bootstrap timeout')), ms),
    ),
  ])
}

/**
 * Strona docelowa redirect_uri Keycloaka. Sama wymiana ?code= na token dzieje
 * się wcześniej w react-oidc-context (trasy montują się dopiero po
 * auth.isLoading === false), więc tutaj zostaje wyłącznie bootstrap aplikacji:
 * pobranie userInfo/ról/firm do storage i wybór strony docelowej.
 */
const PostLogin = (props: PostLoginDispatchProps) => {
  const auth = useAuth()
  const navigate = useNavigate()
  const [bootstrapFailed, setBootstrapFailed] = React.useState(false)
  const [attempt, setAttempt] = React.useState(0)
  const startedForAttempt = React.useRef(-1)

  React.useEffect(() => {
    if (auth.isLoading || auth.error || bootstrapFailed) return
    if (!auth.isAuthenticated) {
      // Ktoś wszedł na /postLogin bez sesji (np. z zakładki) — zwykłe logowanie.
      redirectToLogin()
      return
    }
    if (startedForAttempt.current === attempt) return
    startedForAttempt.current = attempt

    let cancelled = false

    const bootstrap = async () => {
      const [userInfoRaw, roles] = await Promise.all([
        props.getUserInfo(),
        props.getRoles(),
      ])
      // getUserInfo łapie błędy wewnętrznie i zwraca {error: code} zamiast
      // rzucić — bez tej walidacji wywalilibyśmy się dalej na userInfo.roles.
      if (!userInfoRaw || (userInfoRaw as any).error || !roles) {
        throw new Error(
          `getUserInfo/getRoles failed: ${(userInfoRaw as any)?.error ?? 'empty response'}`,
        )
      }
      const companies = userInfoRaw.globalAdmin ? [] : await props.getCompanies()
      return saveUserInfo(userInfoRaw, roles, companies)
    }

    const bootstrapAndNavigate = async () => {
      const userInfo = await withTimeout(bootstrap(), BOOTSTRAP_TIMEOUT_MS)
      if (cancelled) return

      const singleCompanyRole =
        !userInfo.globalAdmin &&
        userInfo.roles?.length === 1 &&
        userInfo.roles[0].company.id != null
          ? userInfo.roles[0].company
          : null

      // Adres sprzed wysłania na logowanie (wygasła sesja, deep link) wraca
      // z Keycloaka w url_state — wróć tam zamiast na domyślny dashboard.
      const urlState = auth.user?.url_state
      const returnTo =
        urlState &&
        urlState.startsWith('/') &&
        !NON_RESTORABLE_ROUTES.some((p) => urlState.startsWith(p))
          ? urlState
          : null

      let returnUrl: string
      // Self-registered Keycloak user with no tenant yet must first choose
      // standard vs service company before checkout. A service-account member holds
      // no roles by design (access is derived from the account's scope), so an empty
      // roles list is only "no access" when they aren't in a service account either.
      if (
        !userInfo.globalAdmin &&
        !userInfo.hasServiceAccountAccess &&
        (!userInfo.roles || userInfo.roles.length === 0)
      ) {
        returnUrl = '/post-register'
      } else if (returnTo) {
        if (singleCompanyRole) props.enterCompany(singleCompanyRole)
        returnUrl = returnTo
      } else if (singleCompanyRole) {
        returnUrl = homeRoutesMap.dashboard.replace(
          ':companyId',
          singleCompanyRole.id.toString(),
        )
        props.enterCompany(singleCompanyRole)
      } else {
        returnUrl = ROUTES.CompanySelection.NoCompany
      }

      navigate(returnUrl, { replace: true })
    }

    bootstrapAndNavigate().catch((err) => {
      console.error('postLogin bootstrap failed:', err)
      if (!cancelled) setBootstrapFailed(true)
    })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isLoading, auth.isAuthenticated, auth.error, bootstrapFailed, attempt])

  if (auth.error) {
    // Nieudana wymiana kodu (np. zużyty ?code= po przywróceniu karty).
    // Świeża runda logowania zamiast automatycznej pętli redirectów.
    return (
      <AuthLayout>
        <Alert variant="error">Błąd autoryzacji</Alert>
        <Alert variant="error">{auth.error.message}</Alert>
        <Button
          type="button"
          variant="primary"
          className="tw-mt-4"
          onClick={() => auth.signinRedirect()}
        >
          Zaloguj ponownie
        </Button>
      </AuthLayout>
    )
  }

  if (bootstrapFailed) {
    // Zalogowanie się udało, ale pobranie danych startowych (userInfo/role/
    // firmy) padło lub przekroczyło limit czasu — daj retry zamiast wiecznego
    // loadera.
    return (
      <AuthLayout>
        <Alert variant="error">Nie udało się pobrać danych użytkownika</Alert>
        <Button
          type="button"
          variant="primary"
          className="tw-mt-4"
          onClick={() => {
            setBootstrapFailed(false)
            setAttempt((a) => a + 1)
          }}
        >
          Spróbuj ponownie
        </Button>
      </AuthLayout>
    )
  }

  return <Loader fullScreen />
}

export default PostLogin
